import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Response object matching old QuestionTableRedux structure
export interface TableResponse {
  rowId: number;
  colId: number;
  value: string | number | null;
  answer?: string | number | { min?: number; max?: number } | any[];
  showFeedback: boolean;
  disabled: boolean;
  feedback?: string;
  options?: any;
  unique?: boolean;
}

// Hint object for feedback
export interface TableHint {
  rowId: number;
  colId: number;
  feedback: string;
}

// Override data for cells
export interface CellOverrideData {
  rowId: number;
  colId: number;
  data: Record<string, any>;
}

// Table state matching old QuestionTableRedux structure
export interface TableState {
  submitDisabled: boolean;
  responses: TableResponse[];
  correct?: boolean;
  hints: TableHint[];
  hintIndex: number;
  showHint: boolean;
  data: CellOverrideData[];
  // Additional fields for backward compatibility
  cellValues: Record<string, CellValue>;
  attemptsLeft?: number;
  isSubmitted?: boolean;
  modifiedCells?: string[];
  isComplete?: boolean;
}

export interface CellValue {
  value: string | number | null;
  isSubmitted: boolean;
}

// Payload interfaces
interface CellChangePayload {
  componentID: string | number;
  id: string; // Format: "rowId_colId"
  value: string | number | null;
  answer?: string | number | { min?: number; max?: number } | any[];
  disabled?: boolean;
  numberOfInputs: number;
  feedback?: string;
  options?: any;
  unique?: boolean;
}

interface ShowHintPayload {
  componentID: string | number;
  showHint: boolean;
}

interface ShowNextHintPayload {
  componentID: string | number;
}

interface OverrideCellDataPayload {
  componentID: string | number;
  id: string; // Format: "rowId_colId"
  data: Record<string, any>;
}

export type TableQuestionState = Record<string, TableState>;

const initialState: TableQuestionState = {};

const getDefaultTableState = (): TableState => ({
  submitDisabled: true,
  responses: [],
  hints: [],
  hintIndex: 0,
  showHint: false,
  data: [],
  cellValues: {},
  isSubmitted: false,
  modifiedCells: [],
});

// Helper function to check if answer is correct
const isCorrectAnswer = (
  response: TableResponse,
  uniqueResponses: (string | number | null)[]
): boolean => {
  const { value, answer, unique } = response;

  // Check for duplicate unique values
  if (unique && uniqueResponses.filter((r) => r === value).length > 1) {
    return false;
  }

  if (answer === undefined) return true;

  if (typeof answer === "string" && typeof value === "string") {
    return answer.toLowerCase() === value.toLowerCase();
  }

  if (typeof answer === "number") {
    return answer == value;
  }

  if (typeof answer === "object" && answer !== null && !Array.isArray(answer)) {
    if ("min" in answer && value !== null) {
      return Number(value) >= answer.min!;
    }
    if ("max" in answer && value !== null) {
      return Number(value) <= answer.max!;
    }
  }

  if (Array.isArray(answer)) {
    return answer.includes(value);
  }

  return true;
};

const tableQuestionSlice = createSlice({
  name: "tableQuestion",
  initialState,
  reducers: {
    // Show hint action - matching old QUESTION_TABLE_SHOW_HINT
    questionTableShowHint: (state, action: PayloadAction<ShowHintPayload>) => {
      const { componentID, showHint } = action.payload;
      const tableKey = String(componentID);

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      state[tableKey].showHint = showHint;
    },

    // Show next hint action - matching old QUESTION_TABLE_SHOW_NEXT_HINT
    questionTableShowNextHint: (
      state,
      action: PayloadAction<ShowNextHintPayload>
    ) => {
      const { componentID } = action.payload;
      const tableKey = String(componentID);

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      state[tableKey].hintIndex++;
    },

    // Cell change action - matching old QUESTION_TABLE_CELL_CHANGE
    questionTableCellChange: (
      state,
      action: PayloadAction<CellChangePayload>
    ) => {
      const {
        componentID,
        id,
        value,
        answer,
        disabled = false,
        numberOfInputs,
        feedback,
        options,
        unique,
      } = action.payload;

      const tableKey = String(componentID);
      const [rowId, colId] = id.split("_").map((i) => parseInt(i, 10));

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      const currentState = state[tableKey];
      let responses = [...currentState.responses];

      // Find existing response index
      const responseIndex = responses.findIndex(
        (response) => response.rowId === rowId && response.colId === colId
      );

      // Create response object
      const responseObject: TableResponse = {
        rowId,
        colId,
        value,
        answer,
        showFeedback: false,
        disabled,
        feedback,
        options,
        unique,
      };

      // Update or add response
      if (responseIndex !== -1) {
        if (
          (typeof value === "number" && isNaN(value)) ||
          (typeof value === "string" && value === null)
        ) {
          responseObject.value = "";
        }
        responses = responses.map((response, index) =>
          index === responseIndex ? responseObject : response
        );
      } else {
        if (
          (typeof value === "number" && !isNaN(value)) ||
          (typeof value === "string" && value !== null) ||
          options
        ) {
          responses = [...responses, responseObject];
        }
      }

      // Check if all inputs are filled
      const allInputsFilled = responses.length === numberOfInputs;

      state[tableKey].responses = responses;
      state[tableKey].submitDisabled = !allInputsFilled;

      // Also update cellValues for backward compatibility
      const cellKey = `${rowId}_${colId}`;
      state[tableKey].cellValues[cellKey] = {
        value,
        isSubmitted: false,
      };
    },

    // Submit action - matching old QUESTION_TABLE_SUBMIT
    questionTableSubmit: (state, action: PayloadAction<string | number>) => {
      const componentID = action.payload;
      const tableKey = String(componentID);

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      const currentState = state[tableKey];
      const responses = [...currentState.responses];
      const hints: TableHint[] = [];

      // Get unique responses for duplicate checking
      const uniqueResponses = responses
        .filter((item) => item.unique === true)
        .map((item) => item.value);

      // Check if all answers are correct
      const isCorrect =
        responses.length > 0 &&
        responses.every((response) =>
          isCorrectAnswer(response, uniqueResponses)
        );

      // Process responses and collect hints
      const disabledResponses = responses.map((item) => {
        const updatedItem = { ...item, showFeedback: true };

        const isDuplicate =
          item.unique &&
          uniqueResponses.filter((r) => r === item.value).length > 1;

        if (isDuplicate) {
          if (item.feedback) {
            hints.push({
              rowId: item.rowId,
              colId: item.colId,
              feedback: item.feedback,
            });
          }
        } else if (isCorrectAnswer(item, uniqueResponses)) {
          updatedItem.disabled = true;
        } else {
          if (item.feedback) {
            hints.push({
              rowId: item.rowId,
              colId: item.colId,
              feedback: item.feedback,
            });
          }
        }

        return updatedItem;
      });

      // Sort hints
      hints.sort((a, b) => {
        if (a.rowId !== b.rowId) {
          return a.rowId - b.rowId;
        }
        return a.colId - b.colId;
      });

      state[tableKey].submitDisabled = true;
      state[tableKey].responses = disabledResponses;
      state[tableKey].correct = isCorrect;
      state[tableKey].hints = hints;
      state[tableKey].hintIndex = 0;
      state[tableKey].isSubmitted = true;

      // Also update cellValues isSubmitted
      Object.keys(state[tableKey].cellValues).forEach((cellKey) => {
        state[tableKey].cellValues[cellKey].isSubmitted = true;
      });
    },

    // Override cell data action - matching old QUESTION_TABLE_OVERRIDE_CELL_DATA
    questionTableOverrideCellData: (
      state,
      action: PayloadAction<OverrideCellDataPayload>
    ) => {
      const { componentID, id, data } = action.payload;
      const tableKey = String(componentID);
      const [rowId, colId] = id.split("_").map((i) => parseInt(i, 10));

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      state[tableKey].data.push({
        rowId,
        colId,
        data,
      });
    },

    // Legacy actions for backward compatibility with new-era-sims
    updateTableCell: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        rowId: number;
        colId: number;
        value: string | number | null;
      }>
    ) => {
      const { tableId, rowId, colId, value } = action.payload;
      const tableKey = String(tableId);

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      const cellKey = `${rowId}_${colId}`;
      const existingCell = state[tableKey].cellValues[cellKey];
      const isSubmitted = existingCell?.isSubmitted || false;

      state[tableKey].cellValues[cellKey] = {
        value,
        isSubmitted,
      };
    },

    resetTable: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        resetAttempts?: boolean;
        initialAttempts?: number;
      }>
    ) => {
      const { tableId, resetAttempts, initialAttempts } = action.payload;
      const tableKey = String(tableId);

      if (state[tableKey]) {
        state[tableKey] = {
          ...getDefaultTableState(),
          attemptsLeft: resetAttempts
            ? initialAttempts
            : state[tableKey].attemptsLeft,
        };
      }
    },

    initializeTable: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        initialValues?: Record<string, CellValue>;
        attemptsLeft?: number;
      }>
    ) => {
      const { tableId, initialValues = {}, attemptsLeft } = action.payload;
      const tableKey = String(tableId);

      state[tableKey] = {
        ...getDefaultTableState(),
        cellValues: state[tableKey]?.cellValues || initialValues,
        attemptsLeft,
      };
    },

    updateTableState: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        attemptsLeft?: number;
        isSubmitted?: boolean;
        modifiedCells?: string[];
      }>
    ) => {
      const { tableId, attemptsLeft, isSubmitted, modifiedCells } =
        action.payload;
      const tableKey = String(tableId);

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      if (attemptsLeft !== undefined) {
        state[tableKey].attemptsLeft = attemptsLeft;
      }
      if (isSubmitted !== undefined) {
        state[tableKey].isSubmitted = isSubmitted;
      }
      if (modifiedCells !== undefined) {
        state[tableKey].modifiedCells = modifiedCells;
      }
    },

    submitTableAnswers: (
      state,
      action: PayloadAction<{
        tableId: string | number;
        correct?: boolean;
      }>
    ) => {
      const { tableId, correct } = action.payload;
      const tableKey = String(tableId);

      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }

      Object.keys(state[tableKey].cellValues).forEach((cellKey) => {
        state[tableKey].cellValues[cellKey].isSubmitted = true;
      });

      state[tableKey].isSubmitted = true;
      if (correct !== undefined) {
        state[tableKey].correct = correct;
      }
    },
    isCompletionRecorded: (
      state,
      action: PayloadAction<{
        tableId: string | number;
      }>
    ) => {
      const tableKey = String(action.payload.tableId);
      if (!state[tableKey]) {
        state[tableKey] = getDefaultTableState();
      }
      state[tableKey].isComplete = true;
    },
  },
});

export const {
  questionTableShowHint,
  questionTableShowNextHint,
  questionTableCellChange,
  questionTableSubmit,
  questionTableOverrideCellData,
  isCompletionRecorded,
  // Legacy exports for backward compatibility
  updateTableCell,
  resetTable,
  initializeTable,
  updateTableState,
  submitTableAnswers,
} = tableQuestionSlice.actions;

export const tableQuestionReducer = tableQuestionSlice.reducer;

// Selectors
export const selectTableCellValue = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number,
  rowId: number,
  colId: number
): string | number | null | undefined => {
  const tableKey = String(tableId);
  const cellKey = `${rowId}_${colId}`;
  return state.tableQuestion[tableKey]?.cellValues[cellKey]?.value;
};

export const selectTableValues = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): Record<string, string | number | null> | undefined => {
  const tableKey = String(tableId);
  const cellValues = state.tableQuestion?.[tableKey]?.cellValues;

  if (!cellValues) return undefined;

  const plainValues: Record<string, string | number | null> = {};
  Object.keys(cellValues).forEach((key) => {
    plainValues[key] = cellValues[key].value;
  });

  return plainValues;
};

export const selectTableAttemptsLeft = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): number | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.attemptsLeft;
};

export const selectTableIsSubmitted = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): boolean | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.isSubmitted;
};

export const selectTableModifiedCells = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): string[] | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.modifiedCells;
};

// New selectors matching old QuestionTableRedux structure
export const selectTableResponses = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): TableResponse[] => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.responses || [];
};

export const selectTableSubmitDisabled = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): boolean => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.submitDisabled ?? true;
};

export const selectTableCorrect = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): boolean | undefined => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.correct;
};

export const selectTableHints = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): TableHint[] => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.hints || [];
};

export const selectTableHintIndex = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): number => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.hintIndex || 0;
};

export const selectTableShowHint = (
  state: { tableQuestion: TableQuestionState },
  tableId: string | number
): boolean => {
  const tableKey = String(tableId);
  return state.tableQuestion?.[tableKey]?.showHint || false;
};
